import { useState, useRef, useEffect } from "react";
import "./css/storeorderalarmmodal.css";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { alarmKeys, useStorePaymentAlarms } from "../../../hooks/useAlarm";
import { formatDateTime } from "../../../utils/commonService";
import { markAlarmAsRead, markPaymentAlarmsAsRead } from "../../../utils/alarmService";
import { IoChevronBack, IoChevronForward } from "../../../components/icons";

// alarmType → 탭 매핑
const TAB_BY_ALARM_TYPE = {
  "주문": "주문",
  "취소 요청": "취소 요청",
};

// alarmType별 표시 문구 (title: 상품명, content: 주문번호)
const MESSAGE_BY_ALARM_TYPE = {
  "주문": ({ title, content }) => `${title} - 주문번호 #${content}`,
  "취소 요청": ({ content }) => `주문번호 #${content} 취소 요청이 들어왔어요`,
};

// 탭 → 전체 읽음 API 파라미터 매핑
const READ_ALL_TYPE_BY_TAB = {
  "주문": "ORDER",
  "취소 요청": "ORDER_CANCEL",
};

const StoreOrderAlarmModal = ({ onClose, buttonRef }) => {
  const [activeTab, setActiveTab] = useState("주문");
  const panelRef = useRef(null);
  const listRef = useRef(null);

  const tabs = ["주문", "취소 요청"];

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading: loading } = useStorePaymentAlarms();

  const alarms = (data?.alarms?.content ?? []).map((alarm) => ({
    id: alarm.storeAlarmIdx,
    tab: TAB_BY_ALARM_TYPE[alarm.alarmType],
    message: (MESSAGE_BY_ALARM_TYPE[alarm.alarmType] ?? (({ title }) => title))(alarm),
    time: formatDateTime(alarm.sendDateTime),
    isUnread: !alarm.readYN,
    link: alarm.alarmLink,
  }));

  const filtered = alarms.filter((item) => item.tab === activeTab);

  // 탭별 읽지 않은 알림 수 (서버 집계값)
  const tabUnreadCounts = {
    "주문": data?.paymentCnt ?? 0,
    "취소 요청": data?.cancelCnt ?? 0,
  };
  const unreadCount = tabUnreadCounts[activeTab];
  // 처리 기한이 임박한 취소 요청
  const expireCancelCount = data?.expireCnt ?? 0;

  // 목록 캐시에서 조건에 맞는 알림을 읽음 처리하고, 서버 집계값(개수)은 재조회
  const applyRead = (shouldMark) => {
    queryClient.setQueryData(alarmKeys.payment(), (old) => {
      if (!old?.alarms?.content) return old;
      return {
        ...old,
        alarms: {
          ...old.alarms,
          content: old.alarms.content.map((a) =>
            shouldMark(a) ? { ...a, readYN: true } : a
          ),
        },
      };
    });
    queryClient.invalidateQueries({ queryKey: alarmKeys.payment() });
    queryClient.invalidateQueries({ queryKey: alarmKeys.unread() });
  };

  const handleMarkAllRead = async () => {
    try {
      await markPaymentAlarmsAsRead(READ_ALL_TYPE_BY_TAB[activeTab]);
      applyRead((a) => TAB_BY_ALARM_TYPE[a.alarmType] === activeTab);
    } catch (error) {
      console.error("주문 알림 전체 읽음 처리 실패:", error);
    }
  };

  const handleItemClick = async (item) => {
    if (item.isUnread) {
      try {
        await markAlarmAsRead(item.id);
        applyRead((a) => a.storeAlarmIdx === item.id);
      } catch (error) {
        console.error("주문 알림 읽음 처리 실패:", error);
      }
    }

    if (item.tab === "주문") {
      // 주문 목록 페이지로 이동하고 orderCode 전달
      navigate("/store/orderlist", {
        state: {
          openModal: true,
          orderCode: item.link,
        },
      });
    } else if (item.tab === "취소 요청") {
      navigate("/store/cancelrequest");
    }
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // 영수증 버튼 클릭은 제외 (버튼 자체의 onClick이 토글을 처리)
        if (buttonRef?.current && buttonRef.current.contains(event.target)) {
          return;
        }
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, buttonRef]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const atTop = scrollTop === 0 && e.deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
      if (atTop || atBottom) {
        e.preventDefault();
      }
      e.stopPropagation();
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <div className="storeorderalarmmodal-panel" ref={panelRef}>
      <div className="storeorderalarmmodal-header">
        <button
          className="storeorderalarmmodal-back-btn"
          onClick={onClose}
          aria-label="닫기"
        >
          <IoChevronBack />
        </button>
        <span className="storeorderalarmmodal-title">주문 알림</span>
        <button
          className="storeorderalarmmodal-readall-btn"
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0}
        >
          모두 읽음
        </button>
      </div>

      <div className="storeorderalarmmodal-tabs">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`storeorderalarmmodal-tab-button ${
              activeTab === tab ? "storeorderalarmmodal-tab-active" : ""
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
            {tabUnreadCounts[tab] > 0 && (
              <span className="storeorderalarmmodal-tab-badge">
                {tabUnreadCounts[tab] > 99 ? "99+" : tabUnreadCounts[tab]}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "취소 요청" && expireCancelCount > 0 && (
        <div className="storeorderalarmmodal-expire-banner">
          <span>
            처리 기한이 임박한 취소 요청이 <b>{expireCancelCount}건</b> 있어요
          </span>
          <IoChevronForward />
        </div>
      )}

      <div className="storeorderalarmmodal-list-container" ref={listRef}>
        {loading ? (
          <div className="storeorderalarmmodal-empty">로딩 중...</div>
        ) : filtered.length === 0 ? (
          <div className="storeorderalarmmodal-empty">알림이 없습니다.</div>
        ) : (
          filtered.map((item) => (
            <div
              key={item.id}
              className={`storeorderalarmmodal-item ${
                item.isUnread ? "storeorderalarmmodal-item-unread" : ""
              }`}
              onClick={() => handleItemClick(item)}
            >
              <div className="storeorderalarmmodal-item-body">
                <div className="storeorderalarmmodal-item-title">{item.message}</div>
                <div className="storeorderalarmmodal-item-time">{item.time}</div>
              </div>
              {item.isUnread && <span className="storeorderalarmmodal-dot"></span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StoreOrderAlarmModal;
